import { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, ExternalLink, Landmark } from 'lucide-react';
import { useIrsReviews, useResolveIrsReview } from '../hooks/useAdminOps';
import { DetailDrawer, QueueTable, StatusChip, type QueueColumn } from '../components/ui';
import type { IrsReviewResolution, IrsSyncReviewItem } from '../api/adminOps';

const NOTES_MAX_LENGTH = 1000;
const VISIBLE_REASON_CHIPS = 2;

const RESOLUTION_OPTIONS: { value: IrsReviewResolution; label: string }[] = [
  { value: 'approved', label: 'Approve — apply the proposed IRS values' },
  { value: 'rejected', label: 'Reject — keep the current values' },
  { value: 'investigating', label: 'Investigating — hold for follow-up' },
];

/** IRS Tax Exempt Organization Search, pre-filtered to the organization's EIN. */
function teosUrl(ein: string): string {
  return `https://apps.irs.gov/app/eos/allSearch?ein1=${encodeURIComponent(ein)}&dispatchMethod=searchAll`;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return {};
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value === '' ? '—' : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-text uppercase tracking-wide">{label}</p>
      <div className="text-sm text-white mt-0.5 break-words">{children}</div>
    </div>
  );
}

/** Two-column previous/proposed comparison with changed keys highlighted. */
function ValueDiff({ previous, proposed }: { previous: unknown; proposed: unknown }) {
  const previousRecord = asRecord(previous);
  const proposedRecord = asRecord(proposed);
  const keys = Array.from(
    new Set([...Object.keys(previousRecord), ...Object.keys(proposedRecord)])
  ).sort();

  if (keys.length === 0) {
    return <p className="text-sm text-gray-text">No field-level changes recorded.</p>;
  }

  return (
    <div className="overflow-x-auto custom-scrollbar">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-border">
            <th className="text-left py-2 pr-3 text-xs font-medium text-gray-text">Field</th>
            <th className="text-left py-2 pr-3 text-xs font-medium text-gray-text">Previous</th>
            <th className="text-left py-2 text-xs font-medium text-gray-text">Proposed</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => {
            const previousValue = previousRecord[key];
            const proposedValue = proposedRecord[key];
            const changed = JSON.stringify(previousValue) !== JSON.stringify(proposedValue);
            return (
              <tr key={key} className="border-b border-gray-border/50 align-top">
                <td className="py-2 pr-3 text-gray-text whitespace-nowrap">{key}</td>
                <td className={`py-2 pr-3 break-all ${changed ? 'text-red-400' : 'text-white'}`}>
                  {formatValue(previousValue)}
                </td>
                <td className={`py-2 break-all ${changed ? 'text-green-400' : 'text-white'}`}>
                  {formatValue(proposedValue)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Organizations() {
  const [selected, setSelected] = useState<IrsSyncReviewItem | null>(null);
  const [resolution, setResolution] = useState<IrsReviewResolution | ''>('');
  const [notes, setNotes] = useState('');

  const queue = useIrsReviews({ pending_only: 'true' });
  const resolve = useResolveIrsReview();

  // Every review starts from a blank decision form.
  useEffect(() => {
    setResolution('');
    setNotes('');
  }, [selected?.id]);

  const trimmedNotes = notes.trim();
  // Approvals adopt IRS data as-is; anything else diverges from the source of
  // record and must document why.
  const notesRequired = resolution === 'rejected' || resolution === 'investigating';
  const canSubmit =
    selected !== null &&
    !resolve.isPending &&
    resolution !== '' &&
    (!notesRequired || trimmedNotes !== '');

  const submitResolution = () => {
    if (selected === null || resolution === '' || !canSubmit) return;
    resolve.mutate(
      {
        id: selected.id,
        resolution,
        notes: trimmedNotes === '' ? undefined : trimmedNotes,
      },
      { onSuccess: () => setSelected(null) }
    );
  };

  const columns: QueueColumn<IrsSyncReviewItem>[] = [
    {
      key: 'organization',
      header: 'Organization',
      render: (row) => (
        <div>
          <p>EIN {row.ein}</p>
          <p className="text-xs text-gray-text font-mono">{row.organization_id}</p>
        </div>
      ),
    },
    {
      key: 'reasons',
      header: 'Held Because',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.reasons.slice(0, VISIBLE_REASON_CHIPS).map((reason) => (
            <StatusChip key={reason} label={reason} tone="warn" />
          ))}
          {row.reasons.length > VISIBLE_REASON_CHIPS && (
            <StatusChip label={`+${row.reasons.length - VISIBLE_REASON_CHIPS}`} tone="neutral" />
          )}
        </div>
      ),
    },
    {
      key: 'sync-job',
      header: 'Sync Job',
      render: (row) => <span className="font-mono text-xs">{row.sync_job_id}</span>,
    },
    {
      key: 'created',
      header: 'Queued',
      render: (row) => (
        <span className="text-gray-text whitespace-nowrap">
          {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
          <Landmark className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Organizations</h1>
          <p className="text-sm text-gray-text">
            IRS sync changes held for review before they reach donor-facing records
          </p>
        </div>
      </div>

      {/* Queue */}
      {queue.isError ? (
        <div className="glass-card p-6">
          <div className="flex items-center space-x-3 text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>Failed to load IRS reviews: {queue.error.message}</span>
          </div>
        </div>
      ) : (
        <QueueTable
          columns={columns}
          rows={queue.rows}
          rowKey={(row) => row.id}
          isLoading={queue.isPending}
          onRowClick={(row) => setSelected(row)}
          emptyTitle="No IRS reviews pending"
          emptyHint="Sync jobs park material organization changes here for review."
          hasNextPage={queue.hasNextPage}
          isFetchingNextPage={queue.isFetchingNextPage}
          onLoadMore={() => queue.fetchNextPage()}
        />
      )}

      {/* Detail drawer */}
      <DetailDrawer
        open={selected !== null}
        title={selected !== null ? `IRS review — EIN ${selected.ein}` : ''}
        onClose={() => setSelected(null)}
        footer={
          selected !== null && (
            <div className="space-y-3">
              <div>
                <label className="label" htmlFor="irs-review-resolution">
                  Resolution
                </label>
                <select
                  id="irs-review-resolution"
                  className="input"
                  value={resolution}
                  onChange={(event) =>
                    setResolution(event.target.value as IrsReviewResolution | '')
                  }
                >
                  <option value="">Select a resolution…</option>
                  {RESOLUTION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label" htmlFor="irs-review-notes">
                  Notes{' '}
                  {notesRequired ? (
                    <span className="text-red-400">(required)</span>
                  ) : (
                    <span className="text-gray-text">(optional)</span>
                  )}
                </label>
                <textarea
                  id="irs-review-notes"
                  className="input min-h-[80px]"
                  maxLength={NOTES_MAX_LENGTH}
                  placeholder="Basis for the decision, for the audit trail"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
                <p className="text-xs text-gray-text mt-1 text-right">
                  {notes.length}/{NOTES_MAX_LENGTH}
                </p>
              </div>
              <button
                onClick={submitResolution}
                disabled={!canSubmit}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resolve.isPending ? 'Submitting…' : 'Submit resolution'}
              </button>
            </div>
          )
        }
      >
        {selected !== null && (
          <>
            <Field label="EIN">
              <span className="inline-flex items-center gap-2">
                <span className="font-mono">{selected.ein}</span>
                <a
                  href={teosUrl(selected.ein)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Verify in IRS TEOS
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </span>
            </Field>
            <Field label="Organization ID">
              <span className="font-mono text-xs">{selected.organization_id}</span>
            </Field>
            <Field label="Sync job">
              <span className="font-mono text-xs">{selected.sync_job_id}</span>
            </Field>
            <Field label="Held because">
              <div className="flex flex-wrap gap-1">
                {selected.reasons.map((reason) => (
                  <StatusChip key={reason} label={reason} tone="warn" />
                ))}
              </div>
            </Field>
            <Field label="Proposed changes">
              <ValueDiff previous={selected.previous_values} proposed={selected.proposed_values} />
            </Field>
            <Field label="Queued">
              {formatDistanceToNow(new Date(selected.created_at), { addSuffix: true })}
            </Field>
          </>
        )}
      </DetailDrawer>
    </div>
  );
}
